import { useCallback, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import { SelectBox } from './Selection';
import s from './SplitLayout.module.css';

/** The list can be dragged wider or narrower, and clicked shut; its width is kept per `key`. */
export interface SplitResize {
  /** Names the stored width (`sixone8.split.<key>`), so each page keeps its own. */
  key: string;
  min?: number;
  max?: number;
}

const DRAG_THRESHOLD = 4;
const KEY_STEP = 16;

function readWidth(key: string): number | null {
  try {
    const raw = localStorage.getItem(`sixone8.split.${key}`);
    const n = raw == null || raw === '' ? NaN : Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function storeWidth(key: string, width: number | null) {
  try {
    if (width == null) localStorage.removeItem(`sixone8.split.${key}`);
    else localStorage.setItem(`sixone8.split.${key}`, String(width));
  } catch {
    // Non-fatal: the width lasts for this load.
  }
}

/**
 * The divider-as-handle shared by every split (`SplitLayout`, `SidePanelLayout`): drag to resize
 * the collapsible pane, click to collapse it and again to bring it back at its last width,
 * double-click to return to the default; arrows resize, Enter or Space toggles, Home resets. The
 * width is kept per key in `localStorage` (`sixone8.split.<key>`).
 *
 * `side` is where the collapsible pane sits: `left` (a list) grows as the handle moves right,
 * `right` (a side panel) as it moves left. Widths are the collapsible pane's: `0` is collapsed,
 * `null` the layout's own default (a `SidePanelLayout`'s half, a share rather than a pixel width).
 */
function useSplitHandle({
  storageKey,
  defaultWidth,
  min,
  max,
  side,
  measure,
}: {
  storageKey?: string;
  defaultWidth: number | null;
  min: number;
  max: number;
  side: 'left' | 'right';
  /** The pane's rendered width, for a drag or key press that starts from the default. */
  measure: () => number;
}) {
  const clamp = useCallback((w: number) => Math.round(Math.min(max, Math.max(min, w))), [min, max]);
  // Below half the minimum a drag snaps the pane shut, so the drag and the click agree on "closed".
  const settle = useCallback((w: number) => (w < min / 2 ? 0 : clamp(w)), [min, clamp]);
  const [width, setWidth] = useState<number | null>(() => {
    const stored = storageKey ? readWidth(storageKey) : null;
    return stored == null ? defaultWidth : settle(stored);
  });
  // The width to come back to after a collapse — the last open one, never the default.
  const lastOpen = useRef<number | null>(width !== 0 ? width : defaultWidth);
  const drag = useRef<{ startX: number; startWidth: number; moved: boolean } | null>(null);
  const [dragging, setDragging] = useState(false);
  const sign = side === 'left' ? 1 : -1;

  const commit = useCallback(
    (w: number | null) => {
      setWidth(w);
      if (w !== 0) lastOpen.current = w;
      if (storageKey) storeWidth(storageKey, w);
    },
    [storageKey],
  );
  const collapsed = width === 0;
  const toggle = () => commit(collapsed ? lastOpen.current : 0);
  const reset = () => commit(defaultWidth);
  // What the pane shows, which is where a drag or a key press starts from: a stored width wider
  // than this window allows is drawn at the ceiling, and a drag from the stored figure would have
  // to travel the difference before anything moved.
  const current = () => (width == null ? measure() : collapsed ? 0 : clamp(width));

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    drag.current = { startX: e.clientX, startWidth: current(), moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD) return;
    if (!d.moved) {
      d.moved = true;
      setDragging(true);
    }
    setWidth(settle(d.startWidth + sign * dx));
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    setDragging(false);
    if (d.moved) commit(settle(d.startWidth + sign * (e.clientX - d.startX)));
    else toggle();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const toward = e.key === 'ArrowRight' ? 1 : -1;
      commit(settle(current() + sign * toward * KEY_STEP));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    } else if (e.key === 'Home') {
      e.preventDefault();
      reset();
    }
  };

  return {
    width,
    collapsed,
    dragging,
    toggle,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onDoubleClick: reset,
      onKeyDown,
    },
  };
}

/**
 * List-plus-detail: the list docked on the left, the detail filling the rest, each scrolling on
 * its own (web_app_design.md §19). Used by Today's split view, Strategies, Watchlists and Alerts.
 *
 * With `resizable`, the divider is a handle: drag it to resize the list, click it to collapse the
 * list (and again to bring it back at its last width), double-click to return to the default.
 * The same from the keyboard — arrows resize, Enter or Space toggles, Home resets — since a handle
 * that only works with a mouse is a handle some people cannot use. A collapsed list is `0`, kept
 * apart from the last open width so reopening lands where the user left it.
 */
export function SplitLayout({
  list,
  listHeader,
  detail,
  listWidth = 380,
  resizable,
  className,
}: {
  list: ReactNode;
  listHeader?: ReactNode;
  detail: ReactNode;
  listWidth?: number;
  resizable?: SplitResize;
  className?: string;
}) {
  const max = resizable?.max ?? 720;
  const split = useSplitHandle({
    storageKey: resizable?.key,
    defaultWidth: listWidth,
    min: resizable?.min ?? 240,
    max,
    side: 'left',
    measure: () => listWidth,
  });
  const width = split.width ?? listWidth;
  const collapsed = split.collapsed;
  return (
    <div className={cx(s.split, split.dragging && s.dragging, className)}>
      <aside className={cx(s.list, collapsed && s.collapsed)} style={{ width }} aria-hidden={collapsed || undefined}>
        {listHeader && <div className={s.listHeader}>{listHeader}</div>}
        <div className={s.listBody}>{list}</div>
      </aside>
      {resizable && (
        <div
          className={cx(s.handle, collapsed && s.handleCollapsed)}
          role="separator"
          aria-orientation="vertical"
          aria-label={collapsed ? 'Show the list' : 'Resize the list'}
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={width}
          tabIndex={0}
          title={collapsed ? 'Show the list' : 'Drag to resize, click to hide'}
          {...split.handleProps}
        >
          <span className={s.grip}>
            <Icon name={collapsed ? 'chevron_right' : 'drag_indicator'} size={collapsed ? 16 : 14} />
          </span>
        </div>
      )}
      <section className={s.detail}>{detail}</section>
    </div>
  );
}

/**
 * Main content with a side panel, inside a page (Markets' Regimes table and chart, Correlation's
 * ranked list and matrix): `SplitLayout`'s handle between two panels. Drag it to resize the panel;
 * click it, or the panel's own hide button (`side`'s `hide`), to slide the panel shut; click the
 * collapsed handle to slide it back at its last width; double-click for the default (half, or
 * `defaultWidth`). Neither side goes below `min`. The panel sits on the right unless `placement` is
 * `left`, where it suits a list sized to its own columns with the main content taking the rest.
 * Below 1100 px the two stack in reading order, and hiding still collapses the panel.
 */
export function SidePanelLayout({
  main,
  side,
  storageKey,
  min = 360,
  placement = 'right',
  defaultWidth,
  label = 'panel',
  className,
}: {
  main: ReactNode;
  /**
   * The panel, given `hide` for the hide button in its header and `collapsed` so it can stop
   * fetching while shut (it stays mounted for the slide, so it would otherwise keep requesting).
   */
  side: (controls: { hide: () => void; collapsed: boolean }) => ReactNode;
  /** Names the stored width (`sixone8.split.<key>`). */
  storageKey: string;
  min?: number;
  /** Which side the panel is on; `left` grows as the handle moves right. */
  placement?: 'left' | 'right';
  /** The panel's opening width in px, for a panel sized to its content; half the box when absent. */
  defaultWidth?: number;
  /** What the panel is, for the handle's accessible name ("Show the chart"). */
  label?: string;
  className?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const pane = useRef<HTMLElement>(null);
  const [boxWidth, setBoxWidth] = useState(0);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const read = () => setBoxWidth(el.clientWidth);
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Room for the main side's own minimum; before the first measure (and under jsdom), no ceiling.
  const max = boxWidth > 0 ? Math.max(min, boxWidth - min) : 4000;
  const left = placement === 'left';
  const split = useSplitHandle({
    storageKey,
    defaultWidth: defaultWidth ?? null,
    min,
    max,
    side: placement,
    measure: () => pane.current?.offsetWidth || (defaultWidth ?? Math.round(boxWidth / 2)),
  });
  const { width, collapsed } = split;
  // A stored width wider than this window allows is held at the ceiling, never overflowed.
  const shown = width == null ? null : collapsed ? 0 : Math.min(width, max);
  const mainPart = <div className={s.sideMain}>{main}</div>;
  const panePart = (
    <section
      ref={pane}
      className={cx(s.sidePane, shown == null && s.sideHalf, collapsed && s.sideCollapsed)}
      style={shown == null ? undefined : { width: shown }}
      aria-hidden={collapsed || undefined}
      inert={collapsed || undefined}
    >
      {/* Kept mounted while shut, so the slide closes over the content rather than an empty box. */}
      {side({ hide: split.toggle, collapsed })}
    </section>
  );
  return (
    <div ref={box} className={cx(s.sideSplit, left && s.sideLeft, split.dragging && s.dragging, className)}>
      {left ? panePart : mainPart}
      <div
        className={cx(s.handle, s.sideHandle, collapsed && s.handleCollapsed)}
        role="separator"
        aria-orientation="vertical"
        aria-label={collapsed ? `Show the ${label}` : `Resize the ${label}'s width`}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={shown ?? Math.round(boxWidth / 2)}
        data-default={width === (defaultWidth ?? null) || undefined}
        tabIndex={0}
        title={collapsed ? `Show the ${label}` : 'Drag to resize, click to hide'}
        {...split.handleProps}
      >
        <span className={s.grip}>
          <Icon name={collapsed ? (left ? 'chevron_right' : 'chevron_left') : 'drag_indicator'} size={collapsed ? 20 : 14} />
        </span>
      </div>
      {left ? mainPart : panePart}
    </div>
  );
}

/** A row's part in a bulk selection (`useSelection`): its box, and whether the list is picking. */
export interface ItemCheck {
  checked: boolean;
  /** Anything in the list is picked: every box shows, not only the hovered one. */
  picking: boolean;
  label: string;
  onToggle: (shift: boolean) => void;
}

/**
 * A selectable row in a split layout's list. With `check`, a checkbox sits beside the row — not
 * inside it, since the row is a button — so the row can be picked for a bulk action.
 */
export function ListItem({
  selected,
  glow,
  onClick,
  children,
  height,
  check,
}: {
  selected?: boolean;
  glow?: boolean;
  onClick: () => void;
  children: ReactNode;
  height?: number;
  check?: ItemCheck;
}) {
  if (check) {
    return (
      <div className={cx(s.checkable, check.picking && s.picking, check.checked && s.picked)}>
        <SelectBox className={s.itemCheck} checked={check.checked} label={check.label} onToggle={check.onToggle} />
        <ListItem selected={selected} glow={glow} onClick={onClick} height={height}>
          {children}
        </ListItem>
      </div>
    );
  }
  return (
    <button
      type="button"
      className={cx(s.item, selected && s.selected, glow && s.glow)}
      style={height ? { minHeight: height } : undefined}
      onClick={onClick}
      aria-current={selected || undefined}
    >
      {children}
    </button>
  );
}

/** Scrolling detail content with page padding. */
export function DetailPane({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx(s.pane, className)}>{children}</div>;
}
